import { ArrowLeft, Building2, ChevronDown, ChevronRight, Search, UserX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '../components/Badge';
import type { UnassignedLead } from '../hooks/useAdminData';
import { useUnassignedLeads } from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatSource(source: string) {
  return source.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface OrgGroup {
  orgId: string;
  orgName: string;
  leads: UnassignedLead[];
}

function OrgLeadSection({ group }: { group: OrgGroup }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-surface rounded-xl border border-border">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-t-xl hover:bg-surface-hover transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown size={16} className="text-text-muted flex-shrink-0" />
        ) : (
          <ChevronRight size={16} className="text-text-muted flex-shrink-0" />
        )}
        <Building2 size={16} className="text-text-muted flex-shrink-0" />
        <span className="text-sm font-semibold text-text">{group.orgName}</span>
        <Badge variant="warning">{group.leads.length} unassigned</Badge>
      </button>

      {expanded && (
        <div className="border-t border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left bg-background/50">
                <th className="px-4 py-2.5 font-medium text-text-muted text-xs">Lead</th>
                <th className="px-4 py-2.5 font-medium text-text-muted text-xs">Stage</th>
                <th className="px-4 py-2.5 font-medium text-text-muted text-xs">Source</th>
                <th className="px-4 py-2.5 font-medium text-text-muted text-xs">Tags</th>
                <th className="px-4 py-2.5 font-medium text-text-muted text-xs">Created</th>
              </tr>
            </thead>
            <tbody>
              {group.leads.map((lead) => (
                <tr key={lead.id} className="border-b border-border last:border-b-0 hover:bg-surface-hover transition-colors">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <UserX size={14} className="text-warning flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-text truncate text-sm">
                          {lead.fullName || lead.email || 'Unknown'}
                        </p>
                        {lead.fullName && lead.email && (
                          <p className="text-xs text-text-muted truncate">{lead.email}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: lead.stageColor ?? '#6b7280' }}
                      />
                      <span className="text-text-secondary text-sm">{lead.stageName}</span>
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant="default">{formatSource(lead.source)}</Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {lead.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-background text-text-muted">
                          {tag}
                        </span>
                      ))}
                      {lead.tags.length > 3 && (
                        <span className="text-[10px] text-text-muted">+{lead.tags.length - 3}</span>
                      )}
                      {lead.tags.length === 0 && <span className="text-text-muted">-</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-text-muted text-xs whitespace-nowrap">
                    {formatDate(lead.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function UnassignedLeads() {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useUnassignedLeads({ page: 1, limit: 500, search: search || undefined });

  const grouped = useMemo<OrgGroup[]>(() => {
    if (!data?.data) return [];
    const map = new Map<string, OrgGroup>();
    for (const lead of data.data) {
      let group = map.get(lead.organizationId);
      if (!group) {
        group = { orgId: lead.organizationId, orgName: lead.organizationName, leads: [] };
        map.set(lead.organizationId, group);
      }
      group.leads.push(lead);
    }
    return Array.from(map.values()).sort((a, b) => b.leads.length - a.leads.length);
  }, [data]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/platform-health')}
          className="p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold">Unassigned Leads</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Leads with no sales rep assigned
            {data && (
              <span className="ml-1 font-medium text-warning">
                ({data.total} across {grouped.length} {grouped.length === 1 ? 'organization' : 'organizations'})
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          placeholder="Search by name, email, company, or org..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-surface border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
        />
      </div>

      {isLoading && (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-surface rounded-xl border border-border p-6 h-20 animate-pulse">
              <div className="h-4 bg-border rounded w-48" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && grouped.length === 0 && (
        <div className="bg-surface rounded-xl border border-border p-12 text-center">
          <UserX size={40} className="mx-auto text-text-muted mb-3" />
          <p className="text-text-muted">No unassigned leads found.</p>
        </div>
      )}

      {!isLoading && grouped.length > 0 && (
        <div className="space-y-4">
          {grouped.map((group) => (
            <OrgLeadSection key={group.orgId} group={group} />
          ))}
        </div>
      )}
    </div>
  );
}
